import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { monthlyPayment, money } from "@/lib/dashboardApi";
import { ShieldCheck, Check, Loader2, Sparkles, Star, Phone } from "lucide-react";
import { FinancingBanner } from "@/components/FinancingBanner";

const LABEL = { good: "GOOD", better: "BETTER", best: "BEST" };
const SUB = { good: "Essential Comfort", better: "Enhanced Comfort", best: "Ultimate Comfort & Protection" };
const TIERS = ["good", "better", "best"];

export default function ProposalView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMonthly, setShowMonthly] = useState(true);
  const [selected, setSelected] = useState("");
  const [ack, setAck] = useState(false);
  const [signName, setSignName] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [depLoading, setDepLoading] = useState(false);
  const payDeposit = async () => {
    setDepLoading(true);
    try { const { data } = await api.post(`/public/proposals/${token}/deposit-link`); window.location.href = data.payment_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment."); setDepLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/proposals/${token}`); setData(data); if (data.selected_tier) { setSelected(data.selected_tier); setDone(data.status === "sold"); } }
      catch { toast.error("Proposal not found"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">This proposal is unavailable.</div>;

  const fin = data.financing || { apr: 9.99, term_months: 120, deposit_pct: 10 };
  const tiers = data.tiers || {};
  const tierTotal = (t) => (t?.total != null ? Number(t.total) : (Number(t?.equipment?.price) || 0) + (t?.addons || []).reduce((s, a) => s + (Number(a.price) || 0), 0));
  const selTier = selected ? tiers[selected] : null;
  const selLifetime = !!selTier?.lifetime;

  const submit = async () => {
    if (!selected) { toast.error("Please select a system option."); return; }
    if (selLifetime && !ack) { toast.error("Please acknowledge the Lifetime Warranty maintenance requirements."); return; }
    if (!signName.trim() || !agree) { toast.error("Please type your name and agree to sign."); return; }
    setSubmitting(true);
    try {
      if (selLifetime) await api.post(`/public/proposals/${token}/acknowledge`, { name: signName });
      await api.post(`/public/proposals/${token}/sign`, { selected_tier: selected, signer_name: signName, signature_img: "", acknowledgment: selLifetime ? { accepted: ack, name: signName } : {} });
      setDone(true); window.scrollTo(0, 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    const total = tierTotal(selTier);
    const deposit = total * (Number(fin.deposit_pct) || 0) / 100;
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center" data-testid="proposal-done">
          <div className="mx-auto h-14 w-14 rounded-full bg-green-100 flex items-center justify-center"><Check className="w-7 h-7 text-green-600" /></div>
          <h1 className="mt-4 text-2xl font-extrabold text-[#12355B]">You're all set, {data.customer_name?.split(" ")[0] || "there"}!</h1>
          <p className="mt-2 text-slate-600">Your <b>{LABEL[selected]}</b> comfort system is confirmed. Our team will reach out to schedule your installation.</p>
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-left text-sm">
            <div className="flex justify-between py-1"><span className="text-slate-500">Total investment</span><b>{money(total)}</b></div>
            <div className="flex justify-between py-1"><span className="text-slate-500">Deposit due</span><b>{money(deposit)}</b></div>
            {selLifetime && <div className="flex justify-between py-1"><span className="text-slate-500">Lifetime Warranty</span><b className="text-[#F97316]">Enrolled + maintenance plan</b></div>}
          </div>
          <p className="mt-4 text-xs text-slate-400">A team member will finalize scheduling. Thank you for choosing Aquila Air &amp; Heating.</p>
          <button onClick={payDeposit} disabled={depLoading} data-testid="pv-pay-deposit" className="mt-5 w-full py-3.5 rounded-lg bg-[#F97316] hover:bg-[#ea6a0c] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{depLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Pay Deposit ({money(deposit)}) Now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="proposal-view">
      <header className="bg-[#12355B] text-white py-5 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <img src="/logo-full.webp" alt="Aquila Air & Heating" className="h-10 w-auto" />
          <a href="tel:+13212921189" className="text-sm font-semibold flex items-center gap-1.5"><Phone className="w-4 h-4" /> (321) 292-1189</a>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <p className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Prepared for {data.customer_name || "you"}</p>
        <h1 className="mt-1 text-3xl font-extrabold text-[#12355B]">{data.title}</h1>
        <div className="mt-4 inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm font-semibold">
          <button onClick={() => setShowMonthly(true)} className={`px-4 py-2 ${showMonthly ? "bg-[#12355B] text-white" : "bg-white text-slate-600"}`} data-testid="pv-monthly">Monthly Payment</button>
          <button onClick={() => setShowMonthly(false)} className={`px-4 py-2 ${!showMonthly ? "bg-[#12355B] text-white" : "bg-white text-slate-600"}`} data-testid="pv-total">Total Investment</button>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          {TIERS.map((key) => {
            const t = tiers[key];
            if (!t || !t.equipment) return (
              <div key={key} className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-slate-400 text-sm">{LABEL[key]} option not configured</div>
            );
            const total = tierTotal(t);
            const monthly = Math.round(t.monthly != null ? t.monthly : monthlyPayment(total, fin.apr, fin.term_months));
            const popular = data.popular_tier === key;
            const recommended = data.recommended_tier === key;
            const isSel = selected === key;
            return (
              <div key={key} className={`relative rounded-2xl bg-white border-2 p-5 flex flex-col ${isSel ? "border-green-500" : recommended ? "border-green-500" : popular ? "border-[#F97316]" : "border-slate-200"}`} data-testid={`pv-tier-${key}`}>
                {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold bg-[#F97316] text-white px-3 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> MOST POPULAR</span>}
                {recommended && <span className="absolute -top-3 right-3 text-[10px] font-bold bg-green-600 text-white px-2 py-1 rounded-full flex items-center gap-1" data-testid={`pv-recommended-${key}`}><Star className="w-3 h-3" /> RECOMMENDED</span>}
                <p className="text-xs font-bold tracking-widest text-slate-400">{LABEL[key]}</p>
                <p className="font-bold text-[#12355B]">{SUB[key]}</p>
                <div className="mt-3 h-40 bg-slate-100 rounded-xl overflow-hidden">{t.equipment.image_url ? <img src={t.equipment.image_url} alt={t.equipment.model} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Sparkles className="w-8 h-8" /></div>}</div>
                <p className="mt-3 font-bold text-slate-900">{t.equipment.brand} {t.equipment.model}</p>
                <p className="text-xs text-slate-500">{t.equipment.system_type} · {t.equipment.tonnage} · {t.equipment.seer2}</p>
                <ul className="mt-3 space-y-1 text-sm text-slate-600 flex-1">
                  {(t.equipment.features || []).slice(0, 5).map((f, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-[#F97316] shrink-0 mt-0.5" />{f}</li>)}
                  {(t.addons || []).map((a) => <li key={a.id} className="flex gap-2"><Check className="w-4 h-4 text-cool shrink-0 mt-0.5" />{a.name}</li>)}
                  {(t.install_kit || []).map((k) => <li key={k.id} className="flex gap-2"><Check className="w-4 h-4 text-cool shrink-0 mt-0.5" />{k.name}</li>)}
                </ul>
                <div className="mt-3 text-xs text-slate-500">
                  <p>{t.equipment.parts_warranty} · {t.equipment.labor_warranty}</p>
                  {t.lifetime ? <p className="font-bold text-[#F97316] flex items-center gap-1 mt-1"><Sparkles className="w-3.5 h-3.5" /> Lifetime Warranty (maintenance plan required)</p> : null}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                  {showMonthly ? <p className="text-3xl font-extrabold text-[#12355B]">${monthly.toLocaleString()}<span className="text-sm text-slate-400 font-medium">/mo*</span></p>
                    : <>
                        {t.discount_total > 0 && <p className="text-base text-slate-400 line-through" data-testid={`pv-orig-${key}`}>{money(t.base_total)}</p>}
                        <p className="text-3xl font-extrabold text-[#12355B]">{money(total)}</p>
                        {(t.discount_lines || []).length > 0 && (
                          <div className="mt-2 text-left inline-block" data-testid={`pv-savings-${key}`}>
                            {(t.discount_lines || []).map((l, i) => <p key={i} className="text-xs text-emerald-700 flex justify-between gap-4"><span>{l.name}</span>{" "}<span>−{money(l.amount)}</span></p>)}
                          </div>
                        )}
                      </>}
                  {showMonthly && t.discount_total > 0 && <p className="mt-1 text-[11px] font-semibold text-emerald-700" data-testid={`pv-monthly-savings-${key}`}>Includes {money(t.discount_total)} in savings</p>}
                </div>
                <button onClick={() => { setSelected(key); setAck(false); }} data-testid={`pv-select-${key}`} className={`mt-4 w-full py-3 rounded-lg font-bold ${isSel ? "bg-green-600 text-white" : "bg-[#F97316] hover:bg-[#ea6a0c] text-white"}`}>{isSel ? "✓ Selected" : "Select This System"}</button>
              </div>
            );
          })}
        </div>

        <FinancingBanner className="mt-8" />

        {/* Selection / acknowledge / sign */}
        {selected && (
          <div className="mt-8 max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 p-6" data-testid="pv-signblock">
            <h2 className="text-xl font-bold text-[#12355B]">Review &amp; Approve — {LABEL[selected]}</h2>
            {selLifetime && (
              <div className="mt-4 rounded-xl bg-orange-50 border border-orange-200 p-4">
                <p className="font-bold text-[#12355B] flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#F97316]" /> Lifetime Warranty Requirements</p>
                <p className="mt-2 text-sm text-slate-700">{data.lifetime_ack_text}</p>
                <p className="mt-2 text-sm text-slate-700">Required plan: <b>{data.required_maintenance_plan}</b></p>
                <label className="mt-3 flex items-start gap-2 text-sm font-medium text-slate-800"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="pv-ack" className="mt-1" /> I understand and agree to maintain the required maintenance plan with two professional maintenance visits per year to keep my Lifetime Warranty eligibility.</label>
              </div>
            )}
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-500">Type your full name to sign *</label>
              <input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="pv-signname" className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#F97316]" placeholder="Full name" />
              <label className="mt-3 flex items-start gap-2 text-sm text-slate-800"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="pv-agree" className="mt-1" /> I agree to purchase the selected system and accept this as my electronic signature.</label>
            </div>
            <button onClick={submit} disabled={submitting} data-testid="pv-submit" className="mt-5 w-full py-3.5 rounded-lg bg-[#12355B] hover:bg-[#0d2847] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Approve &amp; Sign</button>
            <p className="mt-2 text-[11px] text-slate-400 text-center">*Monthly payments are estimates and not a financing offer. Subject to credit approval.</p>
          </div>
        )}
      </div>
    </div>
  );
}
